import { useCallback, useRef, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Campo, Selecao } from '../componentes/forma.tsx';
import { Bancada, Busca, Chave, Lista, Painel } from '../componentes/painel.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { useCadastro, useOpcoes } from '../lib/cadastro.ts';
import { formatarTelefone, paraCampoDinheiro, soDigitos, UFS } from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';
import './medicos.css';

interface Medico {
  id: string;
  nome: string;
  crmUf: string;
  crmNumero: string;
  crm: string;
  email: string | null;
  telefone: string | null;
  especialidades: { id: number; nome: string }[];
  podeLaudar: boolean;
  podeCadastrar: boolean;
  podeAlterar: boolean;
  ativo: boolean;
  temAssinatura: boolean;
  assinaturaEscala: number;
  /** Quanto ele recebe por laudo assinado. */
  valorLaudo: number | null;
}

type Form = {
  nome: string;
  crmUf: string;
  crmNumero: string;
  email: string;
  telefone: string;
  especialidades: number[];
  podeLaudar: boolean;
  podeCadastrar: boolean;
  podeAlterar: boolean;
  ativo: boolean;
  /** Tamanho da assinatura no documento, em % do padrao. */
  assinaturaEscala: number;
  valorLaudo: string;
};

const VAZIO: Form = {
  nome: '', crmUf: 'MG', crmNumero: '', email: '', telefone: '',
  especialidades: [], podeLaudar: true, podeCadastrar: false, podeAlterar: false, ativo: true,
  assinaturaEscala: 100, valorLaudo: "",
};

export function Medicos() {
  const podeEditar = usePode()('pode_medicos');
  const opcoes = useOpcoes();
  const [busca, setBusca] = useState('');
  const [recado, setRecado] = useState('');
  const arquivo = useRef<HTMLInputElement>(null);
  const [versaoAssinatura, setVersaoAssinatura] = useState(0);

  const paraFormulario = useCallback(
    (m: Medico): Form => ({
      nome: m.nome,
      crmUf: m.crmUf,
      crmNumero: m.crmNumero,
      email: m.email ?? '',
      telefone: m.telefone ?? '',
      especialidades: m.especialidades.map((e) => e.id),
      podeLaudar: m.podeLaudar,
      podeCadastrar: m.podeCadastrar,
      podeAlterar: m.podeAlterar,
      ativo: m.ativo,
      assinaturaEscala: m.assinaturaEscala ?? 100,
      valorLaudo: paraCampoDinheiro(m.valorLaudo),
    }),
    [],
  );

  const c = useCadastro<Medico, Form>({
    recurso: 'medicos',
    vazio: VAZIO,
    paraFormulario,
    rotulo: (m) => m.nome,
  });

  const selecionado = c.itens.find((m) => m.id === c.editando);
  const filtrados = busca
    ? c.itens.filter(
        (m) =>
          m.nome.toLowerCase().includes(busca.toLowerCase()) ||
          m.crm.toLowerCase().includes(busca.toLowerCase()),
      )
    : c.itens;

  function alternarEspecialidade(id: number) {
    c.setForm((f) => ({
      ...f,
      especialidades: f.especialidades.includes(id)
        ? f.especialidades.filter((e) => e !== id)
        : [...f.especialidades, id],
    }));
  }

  async function enviarAssinatura(arq: File) {
    if (!c.editando) return;
    setRecado('');
    const dataUrl = await new Promise<string>((resolver, rejeitar) => {
      const leitor = new FileReader();
      leitor.onload = () => resolver(String(leitor.result));
      leitor.onerror = () => rejeitar(new Error('falha ao ler o arquivo'));
      leitor.readAsDataURL(arq);
    });
    try {
      await api.put(`/medicos/${c.editando}/assinatura`, { assinatura: dataUrl });
      setVersaoAssinatura((v) => v + 1);
      setRecado('Assinatura atualizada.');
      void c.lista.refetch();
    } catch (e) {
      c.setErroGeral(
        e instanceof ErroApi ? (e.campos.assinatura ?? e.message) : 'Não foi possível enviar.',
      );
    }
  }

  async function removerAssinatura() {
    if (!c.editando || !confirm('Remover a assinatura deste médico?')) return;
    await api.apagar(`/medicos/${c.editando}/assinatura`);
    setVersaoAssinatura((v) => v + 1);
    setRecado('Assinatura removida.');
    void c.lista.refetch();
  }

  async function redefinirSenha() {
    if (!c.editando) return;
    if (!confirm('Redefinir a senha? Ela volta a ser o CRM e o médico troca no próximo acesso.')) return;
    const r = await api.post<{ senha: string }>(`/medicos/${c.editando}/senha`);
    setRecado(`Senha redefinida para ${r.senha}.`);
  }

  return (
    <Layout>
      <Cabecalho titulo="Médicos" contagem={c.lista.isPending ? '' : `${c.total}`}>
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Novo médico
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? 'Editar médico' : 'Novo médico'}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={recado || c.salvo}
          salvando={c.salvando}
          rotuloCriar="Cadastrar médico"
          aoEnviar={c.enviar}
          aoCancelar={() => {
            setRecado('');
            c.limpar();
          }}
          somenteLeitura={!podeEditar}
          excluindo={c.excluir.isPending}
          aoExcluir={() => {
            if (confirm(`Excluir ${c.form.nome}?`)) c.excluir.mutate(c.editando!);
          }}
        >
          <Campo
            rotulo="Nome"
            obrigatorio
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
            disabled={!podeEditar}
          />

          <Selecao
            rotulo="CRM — UF"
            largura="curta"
            obrigatorio
            value={c.form.crmUf}
            erro={c.campos.crmUf}
            onChange={c.mudar('crmUf')}
            disabled={!podeEditar}
          >
            {UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </Selecao>
          <Campo
            rotulo="CRM — número"
            largura="terco"
            obrigatorio
            inputMode="numeric"
            value={c.form.crmNumero}
            erro={c.campos.crmNumero}
            onChange={c.mudar('crmNumero', soDigitos)}
            disabled={!podeEditar}
            dica={c.editando === null ? 'É o login e a senha inicial.' : undefined}
          />
          <Campo
            rotulo="Telefone"
            largura="curta"
            inputMode="numeric"
            value={formatarTelefone(c.form.telefone)}
            erro={c.campos.telefone}
            onChange={c.mudar('telefone', soDigitos)}
            disabled={!podeEditar}
          />

          {/* O valor do laudo mora aqui, e não no exame nem no procedimento: é
              um acordo com esta pessoa, igual para todo laudo que ela assina.
              O exame recebe uma cópia no momento da assinatura, então mudar
              aqui não reescreve o que já foi assinado. */}
          <Campo
            rotulo="Valor do laudo"
            largura="curta"
            inputMode="numeric"
            placeholder="0,00"
            value={c.form.valorLaudo}
            erro={c.campos.valorLaudo}
            onChange={c.mudar('valorLaudo')}
            disabled={!podeEditar}
            dica="Repassado a ele por laudo assinado."
          />

          <Campo
            rotulo="E-mail"
            type="email"
            value={c.form.email}
            erro={c.campos.email}
            onChange={c.mudar('email')}
            disabled={!podeEditar}
            dica="Recebe avisos de exames aguardando laudo."
          />

          <div className="secao">
            <p className="rotulo secao__titulo">Especialidades</p>
            <div className="marcadores">
              {opcoes.data?.especialidades.map((e) => (
                <label key={e.id} className="marcador">
                  <input
                    type="checkbox"
                    checked={c.form.especialidades.includes(e.id)}
                    onChange={() => alternarEspecialidade(e.id)}
                    disabled={!podeEditar}
                  />
                  <span>{e.nome}</span>
                </label>
              ))}
              {opcoes.data?.especialidades.length === 0 && (
                <p className="campo__dica">Cadastre especialidades primeiro.</p>
              )}
            </div>
          </div>

          <div className="secao">
            <p className="rotulo secao__titulo">O que este médico pode fazer</p>
          </div>
          <Chave
            rotulo="Laudar exames"
            checked={c.form.podeLaudar}
            onChange={c.mudar('podeLaudar')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Cadastrar exames"
            checked={c.form.podeCadastrar}
            onChange={c.mudar('podeCadastrar')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Alterar exames"
            checked={c.form.podeAlterar}
            onChange={c.mudar('podeAlterar')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Ativo"
            checked={c.form.ativo}
            onChange={c.mudar('ativo')}
            disabled={!podeEditar}
          />

          {c.editando !== null && podeEditar && (
            <>
              <div className="secao">
                <p className="rotulo secao__titulo">Assinatura</p>
                {/* A prévia mostra a assinatura NA ALTURA em que vai sair no
                    laudo, e não uma miniatura: ajustar o tamanho olhando para
                    outra escala seria adivinhar. A linha embaixo marca onde o
                    nome e o CRM são impressos, que é o que dá a referência. */}
                <div className="assinatura">
                  {selecionado?.temAssinatura ? (
                    <>
                      <img
                        className="assinatura__imagem"
                        style={
                          {
                            '--assinatura-escala': c.form.assinaturaEscala / 100,
                          } as React.CSSProperties
                        }
                        src={`/api/medicos/${c.editando}/assinatura?v=${versaoAssinatura}`}
                        alt={`Assinatura de ${c.form.nome}`}
                      />
                      <div className="assinatura__linha">
                        <span>{c.form.nome || 'NOME DO MÉDICO'}</span>
                        <span>
                          CRM:{c.form.crmUf}
                          {c.form.crmNumero}
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="assinatura__vazia">Sem assinatura cadastrada</p>
                  )}
                </div>

                {selecionado?.temAssinatura && (
                  <div className="assinatura__tamanho">
                    <label className="rotulo" htmlFor="escala-assinatura">
                      Tamanho no documento
                    </label>
                    <div className="assinatura__controle">
                      <input
                        id="escala-assinatura"
                        type="range"
                        min={50}
                        max={250}
                        step={5}
                        value={c.form.assinaturaEscala}
                        disabled={!podeEditar}
                        onChange={c.mudar('assinaturaEscala', (v) => Number(v))}
                      />
                      <span className="assinatura__percentual num">
                        {c.form.assinaturaEscala}%
                      </span>
                    </div>
                    <p className="campo__dica">
                      Vale para o laudo em texto e para a folha OIT. Salve o cadastro para
                      aplicar — laudos já assinados não mudam.
                    </p>
                  </div>
                )}
                <input
                  ref={arquivo}
                  type="file"
                  accept="image/png"
                  className="so-leitor"
                  onChange={(e) => {
                    const arq = e.target.files?.[0];
                    if (arq) void enviarAssinatura(arq);
                    e.target.value = '';
                  }}
                />
                <div className="assinatura__acoes">
                  <Botao type="button" variante="neutro" tamanho="sm" onClick={() => arquivo.current?.click()}>
                    {selecionado?.temAssinatura ? 'Trocar assinatura' : 'Enviar assinatura'}
                  </Botao>
                  {selecionado?.temAssinatura && (
                    <Botao type="button" variante="perigo" tamanho="sm" onClick={() => void removerAssinatura()}>
                      Remover
                    </Botao>
                  )}
                </div>
                <p className="campo__dica">
                  PNG com fundo transparente, sem carimbo. O nome e o CRM são impressos pelo sistema.
                </p>
              </div>

              <div className="secao">
                <p className="rotulo secao__titulo">Acesso</p>
                <Botao type="button" variante="neutro" tamanho="sm" onClick={() => void redefinirSenha()}>
                  Redefinir senha
                </Botao>
              </div>
            </>
          )}
        </Painel>

        <div>
          <div className="filtros cartao">
            <Busca valor={busca} aoMudar={setBusca} placeholder="Nome ou CRM" />
          </div>

          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              filtrados.length === 0
                ? {
                    titulo: busca ? 'Nenhum médico encontrado' : 'Nenhum médico cadastrado',
                    apoio: busca
                      ? 'Ajuste a busca.'
                      : 'Cadastre os médicos que laudam pelo sistema. O CRM é o login.',
                  }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CRM</th>
                  <th>Especialidades</th>
                  <th>Assinatura</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((m) => (
                  <tr
                    key={m.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === m.id || undefined}
                    data-inativa={!m.ativo || undefined}
                    onClick={() => c.selecionar(m)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        c.selecionar(m);
                      }
                    }}
                  >
                    <td className="tabela__nome">{m.nome}</td>
                    <td className="mono">{m.crm}</td>
                    <td>{m.especialidades.map((e) => e.nome).join(', ') || '—'}</td>
                    <td>{m.temAssinatura ? 'Cadastrada' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>
        </div>
      </Bancada>
    </Layout>
  );
}
